import { Group } from '@app/shared/models/group';
import { Role } from '@app/shared/models/role';
import { User } from '@app/shared/models/user';
import { Action, createReducer, on } from '@ngrx/store';
import { MembersActions } from '../actions';


export const membersFeatureKey = 'members';

export interface State {
  attributableRoles: Role[];
  attributableGroups: any[];

  members: User[];
  membersLoading: boolean;
  membersLoadingError: any;

  creationPending: boolean;

  memberDetails: User;
  memberDetailsLoading: boolean;
  memberDetailsLoadingError: any;

  memberDomains: any[];
  memberDomainsLoading: boolean;
  memberDomainsLoaded: boolean;
  memberDomainsLoadingError: any;

  memberAttributableDomains: any[];
  memberAttributableGroups: any[];

  memberGroups: Group[];
  memberGroupsLoading: boolean;
  memberGroupsLoaded: boolean;
  memberGroupsLoadingError: any;

  memberCreated: User;
  memberCreationPending: boolean;
  memberCreationError: any;

  importCSVProcessing: boolean;
  checkContentFileProcessing: boolean;
  checkContentFileSuccess: boolean;
  checkContentFileError: any;
  importMembersProcessing: boolean;
  importMembersSuccess: boolean;
  importMembersError: any;
  importedMembers: User[];
  membersToImport: any[];
  importMembersNonBlockingError: any;
  importCSVSuccess: boolean;
  importCSVError: any;

  memberUpdated: User;
  memberEditionPending: boolean;
  memberEditionError: any;

  memberDeleted: boolean;
  inactivatingMember: boolean;
  inactivatingMemberError: any;

  deleteMemberProcessing: boolean;
  deleteMemberSuccess: boolean;
  deleteMemberError: any;

  attachTeamProcessing: boolean;
  attachTeamSuccess: boolean;
  attachTeamError: any;

  detachTeamProcessing: boolean;
  detachTeamSuccess: boolean;
  detachTeamError: any;

  attachGroupProcessing: boolean;
  attachGroupSuccess: boolean;
  attachGroupError: any;

  detachGroupProcessing: boolean;
  detachGroupSuccess: boolean;
  detachGroupError: any;

  switchRoleProcessing: boolean;
  switchRoleSuccess: boolean;
  switchRoleError: any;

  transferCountSeries: any[];
  transferCountSeriesLoaded: boolean;
  transferCountSeriesLoading: boolean;
  transferCountSeriesLoadingFailure: boolean;

  sizeCountSeries: any[];
  sizeCountSeriesLoaded: boolean;
  sizeCountSeriesLoading: boolean;
  sizeCountSeriesLoadingFailure: boolean;

  fileCountSeries: any[];
  fileCountSeriesLoaded: boolean;
  fileCountSeriesLoading: boolean;
  fileCountSeriesLoadingFailure: boolean;

  memberAttachRootRoleProcessing: boolean;
  memberAttachRootRoleSuccess: boolean;
  memberAttachRootRoleError: any;

  memberDetachRootRoleProcessing: boolean;
  memberDetachRootRoleSuccess: boolean;
  memberDetachRootRoleError: any;
}

export const initialState: State = {
  attributableRoles: [],
  attributableGroups: [],

  members: [],
  membersLoading: false,
  membersLoadingError: null,

  creationPending: false,

  memberDetails: null,
  memberDetailsLoading: null,
  memberDetailsLoadingError: null,

  memberDomains: [],
  memberDomainsLoading: false,
  memberDomainsLoaded: false,
  memberDomainsLoadingError: null,

  memberAttributableDomains: [],
  memberAttributableGroups: [],

  memberGroups: [],
  memberGroupsLoading: false,
  memberGroupsLoaded: false,
  memberGroupsLoadingError: null,

  memberDeleted: null,

  memberCreated: null,
  memberCreationPending: false,
  memberCreationError: null,

  importCSVProcessing: false,
  checkContentFileProcessing: false,
  checkContentFileSuccess: false,
  checkContentFileError: null,
  importMembersProcessing: false,
  importMembersSuccess: false,
  importMembersError: null,
  importedMembers: [],
  membersToImport: [],
  importMembersNonBlockingError: null,
  importCSVSuccess: false,
  importCSVError: null,

  memberUpdated: null,
  memberEditionPending: false,
  memberEditionError: null,
  inactivatingMember: false,
  inactivatingMemberError: null,

  deleteMemberProcessing: false,
  deleteMemberSuccess: false,
  deleteMemberError: null,

  attachTeamProcessing: false,
  attachTeamSuccess: false,
  attachTeamError: null,

  detachTeamProcessing: false,
  detachTeamSuccess: false,
  detachTeamError: null,

  attachGroupProcessing: false,
  attachGroupSuccess: false,
  attachGroupError: null,

  detachGroupProcessing: false,
  detachGroupSuccess: false,
  detachGroupError: null,

  switchRoleProcessing: false,
  switchRoleSuccess: false,
  switchRoleError: null,

  transferCountSeries: [],
  transferCountSeriesLoaded: false,
  transferCountSeriesLoading: false,
  transferCountSeriesLoadingFailure: null,

  sizeCountSeries: [],
  sizeCountSeriesLoaded: false,
  sizeCountSeriesLoading: false,
  sizeCountSeriesLoadingFailure: null,

  fileCountSeries: [],
  fileCountSeriesLoaded: false,
  fileCountSeriesLoading: false,
  fileCountSeriesLoadingFailure: null,

  memberAttachRootRoleProcessing: false,
  memberAttachRootRoleSuccess: false,
  memberAttachRootRoleError: null,

  memberDetachRootRoleProcessing: false,
  memberDetachRootRoleSuccess: false,
  memberDetachRootRoleError: null,
};

const membersReducer = createReducer(
  initialState,
  // Load Team Members
  on(MembersActions.loadMembers, (state) => ({
    ...state,
    members: initialState.members,
    membersLoadingError: initialState.membersLoadingError,
    membersLoading: true,
    memberEditionError: null
  })),
  on(MembersActions.loadMembersSuccess, (state, { members }) => ({
    ...state,
    members,
    membersLoading: false
  })),
  on(MembersActions.loadMembersFailure, (state, { error }) => ({
    ...state,
    membersLoadingError: error,
    membersLoading: false
  })),
  on(MembersActions.resetCreation, (state) => ({
    ...state,
    memberCreated: initialState.memberCreated,
    memberCreationError: initialState.memberCreationError,
  })),
  // Load Member Details
  on(MembersActions.loadMember, (state) => ({
    ...state,
    memberDetails: initialState.memberDetails,
    memberEditionPending: initialState.memberEditionPending,
    memberDetailsLoadingError: initialState.memberDetailsLoadingError,
    memberDetailsLoading: true,
  })),
  on(MembersActions.loadMemberSuccess, (state, { member }) => ({
    ...state,
    memberDetails: member,
    memberDetailsLoading: false,
  })),
  on(MembersActions.loadMemberFailure, (state, { error }) => ({
    ...state,
    memberDetailsLoadingError: error,
    memberDetailsLoading: false
  })),
  // Load Member Domains
  on(MembersActions.loadMemberDomains, (state) => ({
    ...state,
    memberDomains: initialState.memberDomains,
    memberDomainsLoaded: initialState.memberDomainsLoaded,
    memberDomainsLoadingError: initialState.memberDomainsLoadingError,
    memberDomainsLoading: true,
  })),
  on(MembersActions.loadMemberDomainsSuccess, (state, { domains, attributableDomains }) => ({
    ...state,
    memberDomains: domains,
    memberDomainsLoading: false,
    memberAttributableDomains: attributableDomains,
  })),
  on(MembersActions.loadMemberDomainsFailure, (state, { error }) => ({
    ...state,
    memberDomainsLoadingError: error,
    memberDomainsLoading: false
  })),
  // Load Member Groups
  on(MembersActions.loadMemberGroups, (state) => ({
    ...state,
    memberGroups: initialState.memberGroups,
    memberGroupsLoaded: initialState.memberGroupsLoaded,
    memberGroupsLoadingError: initialState.memberGroupsLoadingError,
    memberGroupsLoading: true,
  })),
  on(MembersActions.loadMemberGroupsSuccess, (state, { groups, attributableGroups }) => ({
    ...state,
    memberGroups: groups,
    memberGroupsLoading: false,
    memberAttributableGroups: attributableGroups,
  })),
  on(MembersActions.loadMemberGroupsFailure, (state, { error }) => ({
    ...state,
    memberGroupsLoadingError: error,
    memberGroupsLoading: false
  })),
  // Create member
  on(MembersActions.addMember, (state) => ({
    ...state,
    memberCreated: initialState.memberCreated,
    memberCreationError: initialState.memberCreationError,
    membersLoadingError: initialState.membersLoadingError,
    memberCreationPending: true
  })),
  on(MembersActions.addMemberSuccess, (state, { member }) => ({
    ...state,
    memberCreated: member,
    memberCreationPending: false
  })),
  on(MembersActions.addMemberFailure, (state, { error }) => ({
    ...state,
    memberCreationError: error,
    memberCreationPending: false
  })),
  // Import member
  on(MembersActions.resetImportMembers, (state) => ({
    ...state,
    importCSVProcessing: initialState.importCSVProcessing,
    importCSVSuccess: initialState.importCSVSuccess,
    importCSVError: initialState.importCSVError,
    importedMembers: initialState.importedMembers,
    membersToImport: initialState.membersToImport,
    checkContentFileProcessing: initialState.checkContentFileProcessing,
    checkContentFileSuccess: initialState.checkContentFileSuccess,
    checkContentFileError: initialState.checkContentFileError,
    importMembersProcessing: initialState.importMembersProcessing,
    importMembersSuccess: initialState.importMembersSuccess,
    importMembersError: initialState.importMembersError,
  })),
  on(MembersActions.initImportMembers, (state) => ({
    ...state,
    importCSVProcessing: true,
    importCSVSuccess: initialState.importCSVSuccess,
    importCSVError: initialState.importCSVError,
    importedMembers: initialState.importedMembers,
    membersToImport: initialState.membersToImport,
    checkContentFileProcessing: initialState.checkContentFileProcessing,
    checkContentFileSuccess: initialState.checkContentFileSuccess,
    checkContentFileError: initialState.checkContentFileError,
    importMembersProcessing: initialState.importMembersProcessing,
    importMembersSuccess: initialState.importMembersSuccess,
    importMembersError: initialState.importMembersError,
    importMembersNonBlockingError: initialState.importMembersError,
  })),
  on(MembersActions.checkContentFile, (state) => ({
    ...state,
    checkContentFileProcessing: true,
  })),
  on(MembersActions.checkContentFileFailure, (state, { error }) => ({
    ...state,
    checkContentFileProcessing: false,
    checkContentFileError: error,
  })),
  on(MembersActions.importMembers, (state, { members }) => ({
    ...state,
    importMembersProcessing: true,
    checkContentFileProcessing: false,
    membersToImport: members,
  })),
  on(MembersActions.importMembersSuccess, (state, { members, nonBlockingError }) => ({
    ...state,
    importMembersSuccess: true,
    importCSVSuccess: true,
    importedMembers: members,
    importMembersProcessing: initialState.importMembersProcessing,
    importMembersNonBlockingError: nonBlockingError,
  })),
  on(MembersActions.importMembersFailure, (state, { error }) => ({
    ...state,
    importMembersProcessing: false,
    importMembersError: error,
  })),
  on(MembersActions.UpdateImportMembersStatus, (state, { importedMembers }) => ({
    ...state,
    importedMembers,
  })),
  // Attach member to team
  on(MembersActions.attachMemberToTeam, (state) => ({
    ...state,
    attachTeamProcessing: true,
    attachTeamSuccess: initialState.attachTeamSuccess,
    attachTeamError: initialState.attachTeamError,
  })),
  on(MembersActions.attachMemberToTeamSuccess, (state) => ({
    ...state,
    attachTeamProcessing: initialState.attachTeamProcessing,
    attachTeamSuccess: true,
  })),
  on(MembersActions.attachMemberToTeamFailure, (state, { error }) => ({
    ...state,
    attachTeamProcessing: initialState.attachTeamProcessing,
    attachTeamSuccess: false,
    attachTeamError: error,
  })),
  // Detach member from team
  on(MembersActions.detachMemberFromTeam, (state) => ({
    ...state,
    detachTeamProcessing: true,
    detachTeamSuccess: initialState.detachTeamSuccess,
    detachTeamError: initialState.detachTeamError,
  })),
  on(MembersActions.detachMemberFromTeamSuccess, (state) => ({
    ...state,
    detachTeamProcessing: initialState.detachTeamProcessing,
    detachTeamSuccess: true,
  })),
  on(MembersActions.detachMemberFromTeamFailure, (state, { error }) => ({
    ...state,
    detachTeamProcessing: initialState.detachTeamProcessing,
    detachTeamSuccess: false,
    detachTeamError: error,
  })),
  // Attach member to group
  on(MembersActions.attachMemberToGroup, (state) => ({
    ...state,
    attachGroupProcessing: true,
    attachGroupSuccess: initialState.attachGroupSuccess,
    attachGroupError: initialState.attachGroupError,
  })),
  on(MembersActions.attachMemberToGroupSuccess, (state) => ({
    ...state,
    attachGroupProcessing: initialState.attachGroupProcessing,
    attachGroupSuccess: true,
  })),
  on(MembersActions.attachMemberToGroupFailure, (state, { error }) => ({
    ...state,
    attachGroupProcessing: initialState.attachGroupProcessing,
    attachGroupSuccess: false,
    attachGroupError: error,
  })),
  // Detach member from group
  on(MembersActions.detachMemberFromGroup, (state) => ({
    ...state,
    detachGroupProcessing: true,
    detachGroupSuccess: initialState.detachGroupSuccess,
    detachGroupError: initialState.detachGroupError,
  })),
  on(MembersActions.detachMemberFromGroupSuccess, (state) => ({
    ...state,
    detachGroupProcessing: initialState.detachGroupProcessing,
    detachGroupSuccess: true,
  })),
  on(MembersActions.detachMemberFromGroupFailure, (state, { error }) => ({
    ...state,
    detachGroupProcessing: initialState.detachGroupProcessing,
    detachGroupSuccess: false,
    detachGroupError: error,
  })),
  // Update member
  on(MembersActions.updateMember, (state) => ({
    ...state,
    memberUpdated: initialState.memberUpdated,
    memberEditionPending: true,
    memberEditionError: initialState.memberEditionError,
  })),
  on(MembersActions.updateMemberSuccess, (state, { member }) => ({
    ...state,
    memberDetails: { ...state.memberDetails, ...member },
    memberEditionPending: false
  })),
  on(MembersActions.updateMemberFailure, (state, { error }) => ({
    ...state,
    memberEditionPending: false,
    memberEditionError: error,
  })),
  // Load Attributable Roles
  on(MembersActions.loadAttributableRoles, (state) => ({
    ...state,
    attributableRoles: [],
  })),
  on(MembersActions.loadAttributableRolesSuccess, (state, { roles }) => ({
    ...state,
    attributableRoles: roles,
  })),
  on(MembersActions.loadAttributableRolesFailure, (state, { error }) => ({
    ...state,
    attributableRolesError: error,
  })),
  // Load Attributable Groups
  on(MembersActions.loadAttributableGroups, (state) => ({
    ...state,
    attributableGroups: [],
  })),
  on(MembersActions.loadAttributableGroupsSuccess, (state, { groups }) => ({
    ...state,
    attributableGroups: groups,
  })),
  on(MembersActions.loadAttributableGroupsFailure, (state, { error }) => ({
    ...state,
    attributableGroupsError: error,
  })),
  // Delete Member
  on(MembersActions.deleteMember, (state) => ({
    ...state,
    deleteMemberProcessing: true,
    deleteMemberSuccess: initialState.deleteMemberSuccess,
    deleteMemberError: initialState.deleteMemberError
  })),
  on(MembersActions.deleteMemberSuccess, (state) => ({
    ...state,
    deleteMemberProcessing: false,
    deleteMemberSuccess: true,
  })),
  on(MembersActions.deleteMemberFailure, (state, { error }) => ({
    ...state,
    deleteMemberProcessing: false,
    deleteMemberError: error,
  })),
  on(MembersActions.switchMemberRole, (state) => ({
    ...state,
    switchRoleProcessing: true,
    switchRoleSuccess: initialState.switchRoleSuccess,
    switchRoleError: initialState.switchRoleError,
  })),
  on(MembersActions.switchMemberRoleSuccess, (state) => ({
    ...state,
    switchRoleProcessing: initialState.switchRoleProcessing,
    switchRoleSuccess: true,
  })),
  on(MembersActions.switchMemberRoleFailure, (state, { error }) => ({
    ...state,
    switchRoleProcessing: initialState.switchRoleProcessing,
    switchRoleSuccess: false,
    switchRoleError: error,
  })),
  on(MembersActions.loadMemberTransferCount, (state) => ({
    ...state,
    transferCountSeries: initialState.transferCountSeries,
    transferCountSeriesLoading: true,
    transferCountSeriesLoaded: initialState.transferCountSeriesLoaded,
    transferCountSeriesLoadingFailure: initialState.transferCountSeriesLoadingFailure,
  })),
  on(MembersActions.loadMemberTransferCountSuccess, (state, { transferCountSeries }) => ({
    ...state,
    transferCountSeries,
    transferCountSeriesLoading: false,
    transferCountSeriesLoaded: true,
    transferCountSeriesLoadingFailure: initialState.transferCountSeriesLoadingFailure,
  })),
  on(MembersActions.loadMemberTransferCountFailure, (state, { error }) => ({
    ...state,
    transferCountsLoading: false,
    transferCountsLoadingFailure: true,
  })),
  on(MembersActions.loadMemberSizeCount, (state) => ({
    ...state,
    sizeCountSeries: initialState.sizeCountSeries,
    sizeCountSeriesLoading: true,
    sizeCountSeriesLoaded: initialState.sizeCountSeriesLoaded,
    sizeCountSeriesLoadingFailure: initialState.sizeCountSeriesLoadingFailure,
  })),
  on(MembersActions.loadMemberSizeCountSuccess, (state, { sizeCountSeries }) => ({
    ...state,
    sizeCountSeries,
    sizeCountSeriesLoading: false,
    sizeCountSeriesLoaded: true,
    sizeCountSeriesLoadingFailure: initialState.sizeCountSeriesLoadingFailure,
  })),
  on(MembersActions.loadMemberSizeCountFailure, (state, { error }) => ({
    ...state,
    sizeCountsLoading: false,
    sizeCountsLoadingFailure: true,
  })),
  on(MembersActions.loadMemberFileCount, (state) => ({
    ...state,
    fileCountSeries: initialState.fileCountSeries,
    fileCountSeriesLoading: true,
    fileCountSeriesLoaded: initialState.fileCountSeriesLoaded,
    fileCountSeriesLoadingFailure: initialState.fileCountSeriesLoadingFailure,
  })),
  on(MembersActions.loadMemberFileCountSuccess, (state, { fileCountSeries }) => ({
    ...state,
    fileCountSeries,
    fileCountSeriesLoading: false,
    fileCountSeriesLoaded: true,
    fileCountSeriesLoadingFailure: initialState.fileCountSeriesLoadingFailure,
  })),
  on(MembersActions.loadMemberFileCountFailure, (state, { error }) => ({
    ...state,
    fileCountsLoading: false,
    fileCountsLoadingFailure: true,
  })),
  // Root Role
  on(MembersActions.addRootRoleToMember, (state) => ({
    ...state,
    memberAttachRootRoleProcessing: true,
    memberAttachRootRoleSuccess: initialState.memberAttachRootRoleSuccess,
    memberAttachRootRoleError: initialState.memberAttachRootRoleError,
  })),
  on(MembersActions.addRootRoleToMemberSuccess, (state) => ({
    ...state,
    memberAttachRootRoleProcessing: initialState.memberAttachRootRoleProcessing,
    memberAttachRootRoleSuccess: true,
  })),
  on(MembersActions.addRootRoleToMemberFailure, (state, { error }) => ({
    ...state,
    memberAttachRootRoleProcessing: initialState.memberAttachRootRoleProcessing,
    memberAttachRootRoleError: error,
  })),
  on(MembersActions.removeRootRoleFromMember, (state) => ({
    ...state,
    memberDetachRootRoleProcessing: true,
    memberDetachRootRoleSuccess: initialState.memberDetachRootRoleSuccess,
    memberDetachRootRoleError: initialState.memberDetachRootRoleError,
  })),
  on(MembersActions.removeRootRoleFromMemberSuccess, (state) => ({
    ...state,
    memberDetachRootRoleProcessing: initialState.memberDetachRootRoleProcessing,
    memberDetachRootRoleSuccess: true,
  })),
  on(MembersActions.removeRootRoleFromMemberFailure, (state, { error }) => ({
    ...state,
    memberDetachRootRoleProcessing: initialState.memberDetachRootRoleProcessing,
    memberDetachRootRoleError: error,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return membersReducer(state, action);
}

